import { memo, useEffect, useState } from 'react'
import { ColorSwatch, Group, ScrollArea, Stack, Text, TextInput, UnstyledButton } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import type { DetectionComparisonEntity } from '@/api/main/types.gen'
import useDebounce from '@/hooks/useDebounceSearch'
import { entityColor, entityLabel, formatMetres, visibleMembers } from './comparison'

const MAX_LISTED = 600
const LIST_HEIGHT = 360
const SEARCH_DEBOUNCE_MS = 250

type Props = {
  entities: DetectionComparisonEntity[]
  firstDriveId: number
  visibleDrives: ReadonlySet<number>
  selectedId: string | null
  /** Initial search text; later changes flow from the input to `onSearchChange` only. */
  search: string
  onSearchChange: (value: string) => void
  onSelect: (entity: DetectionComparisonEntity) => void
}

type RowProps = {
  entity: DetectionComparisonEntity
  firstDriveId: number
  visibleDrives: ReadonlySet<number>
  selected: boolean
  onSelect: (entity: DetectionComparisonEntity) => void
}

const EntityRow = memo(function EntityRow({ entity, firstDriveId, visibleDrives, selected, onSelect }: RowProps) {
  const { t } = useTranslation('detectionComparison')
  const members = visibleMembers(entity, visibleDrives)
  const meta =
    members.length === 2
      ? `${formatMetres(entity.separation_m)}${entity.is_same_drive ? ` · ${t('list.oneDrive')}` : ''}`
      : t('display.drive', { id: members[0]?.drive_id })

  return (
    <UnstyledButton
      onClick={() => onSelect(entity)}
      px="xs"
      py={4}
      style={{
        borderRadius: 'var(--mantine-radius-sm)',
        background: selected ? 'var(--mantine-color-default-hover)' : undefined,
      }}
    >
      <Group gap="xs" wrap="nowrap">
        <ColorSwatch color={entityColor(entity, firstDriveId)} size={10} />
        <Text size="sm" fw={500} style={{ flex: 1 }} truncate>
          {entityLabel(entity) || t('list.noPlate')}
        </Text>
        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {meta}
        </Text>
      </Group>
    </UnstyledButton>
  )
})

type RowsProps = Omit<Props, 'search' | 'onSearchChange'>

/** Memoised so that typing into the search box does not re-render hundreds of rows. */
const EntityRows = memo(function EntityRows({
  entities,
  firstDriveId,
  visibleDrives,
  selectedId,
  onSelect,
}: RowsProps) {
  const { t } = useTranslation('detectionComparison')
  const listed = entities.slice(0, MAX_LISTED)

  return (
    <ScrollArea h={LIST_HEIGHT} type="auto">
      <Stack gap={2}>
        {listed.length === 0 && (
          <Text size="sm" c="dimmed">
            {t('list.empty')}
          </Text>
        )}
        {listed.map(entity => (
          <EntityRow
            key={entity.id}
            entity={entity}
            firstDriveId={firstDriveId}
            visibleDrives={visibleDrives}
            selected={entity.id === selectedId}
            onSelect={onSelect}
          />
        ))}
        {entities.length > MAX_LISTED && (
          <Text size="sm" c="dimmed">
            {t('list.more', { count: entities.length - MAX_LISTED })}
          </Text>
        )}
      </Stack>
    </ScrollArea>
  )
})

export function EntityList({
  entities,
  firstDriveId,
  visibleDrives,
  selectedId,
  search,
  onSearchChange,
  onSelect,
}: Props) {
  const { t } = useTranslation('detectionComparison')
  // The input stays responsive while the expensive filtering waits for a typing pause.
  const [draft, setDraft] = useState(search)
  const debouncedDraft = useDebounce(draft, SEARCH_DEBOUNCE_MS)

  useEffect(() => {
    onSearchChange(debouncedDraft)
  }, [debouncedDraft, onSearchChange])

  return (
    <Stack gap="sm">
      <Text fw={600}>{t('list.section')}</Text>
      <TextInput
        placeholder={t('list.searchPlaceholder')}
        value={draft}
        onChange={event => setDraft(event.currentTarget.value)}
      />
      <EntityRows
        entities={entities}
        firstDriveId={firstDriveId}
        visibleDrives={visibleDrives}
        selectedId={selectedId}
        onSelect={onSelect}
      />
    </Stack>
  )
}
